import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import USDateInput from "../components/USDateInput";
import { formatUSD, parseUSD } from "../lib/currency";

const CATEGORIES = ["Maintenance", "Utilities", "Insurance", "Taxes", "Management", "Repairs", "Other"];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const EMPTY = { property_id: "", vendor_id: "", category: "Maintenance", amount: 0, description: "", date: new Date().toISOString().slice(0,10) };
  const [form, setForm] = useState(EMPTY);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY, date: new Date().toISOString().slice(0,10) });
    setOpen(true);
  };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      property_id: row.property_id || "",
      vendor_id: row.vendor_id || "",
      category: row.category || "Maintenance",
      amount: row.amount,
      description: row.description || "",
      date: row.date,
    });
    setOpen(true);
  };

  const load = async () => {
    const [e, p, v] = await Promise.all([
      api.get("/expenses"),
      api.get("/properties"),
      api.get("/vendors").catch(() => ({ data: { items: [] } })),
    ]);
    setItems(e.data.sort((a,b) => b.date.localeCompare(a.date)));
    setProperties(p.data);
    setVendors(v.data.items || []);
  };
  useEffect(() => { load(); }, []);

  // When the user picks a vendor, auto-fill the category from the
  // vendor's own category — saves a step and keeps the analytics
  // breakdown honest. Users can still override afterward.
  const pickVendor = (vendor_id) => {
    const v = vendors.find((x) => x.id === vendor_id);
    setForm((f) => ({
      ...f,
      vendor_id,
      category: v?.category && CATEGORIES.includes(v.category) ? v.category : f.category,
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        vendor_id: form.vendor_id || null,
      };
      if (editingId) {
        await api.put(`/expenses/${editingId}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post("/expenses", payload);
        toast.success("Expense recorded");
      }
      setOpen(false);
      setEditingId(null);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id, desc) => {
    if (!window.confirm(`Delete expense${desc ? ` "${desc}"` : ""}?`)) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const propMap = useMemo(() => Object.fromEntries(properties.map(p => [p.id, p.name])), [properties]);
  const vendorMap = useMemo(() => Object.fromEntries(vendors.map(v => [v.id, v.name])), [vendors]);
  const total = items.reduce((s, i) => s + i.amount, 0);

  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline text-slate-500">Ledger</div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>Expenses</h1>
            <p className="text-sm text-slate-500 mt-1">Total: <span className="font-mono font-bold text-slate-900">${total.toLocaleString()}</span></p>
          </div>
          <button data-testid="add-expense-button" onClick={openNew} className="btn-primary-dark flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> Log Expense
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="expenses-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Date</Th><Th>Property</Th><Th>Vendor</Th><Th>Category</Th><Th>Description</Th>
                <Th className="text-right">Amount</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="text-center py-16 text-slate-400">No expenses logged yet.</td></tr>}
              {items.map((i) => (
                <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`expense-row-${i.id}`}>
                  <Td className="font-mono text-xs text-slate-600">{i.date}</Td>
                  <Td className="font-semibold text-slate-900">{propMap[i.property_id] || "—"}</Td>
                  <Td className="text-slate-700" data-testid={`expense-vendor-${i.id}`}>{vendorMap[i.vendor_id] || <span className="text-slate-400">—</span>}</Td>
                  <Td><span className="overline text-slate-500">{i.category}</span></Td>
                  <Td className="text-slate-600">{i.description || "—"}</Td>
                  <Td className="text-right font-mono font-bold">${i.amount.toLocaleString()}</Td>
                  <Td className="text-right">
                    <button onClick={()=>openEdit(i)} className="text-slate-500 hover:text-slate-900 p-1 mr-1" data-testid={`edit-expense-${i.id}`} title="Edit"><Pencil className="w-4 h-4" /></button>
                    <button onClick={()=>remove(i.id, i.description)} className="text-slate-500 hover:text-red-600 p-1" data-testid={`delete-expense-${i.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY); } }}>
          <DialogContent>
            <DialogHeader><DialogTitle style={{fontFamily:"Outfit"}}>{editingId ? "Edit expense" : "Log expense"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <Field label="Property">
                <select required className="input-clean w-full" data-testid="expense-property-select" value={form.property_id} onChange={(e)=>setForm({...form, property_id:e.target.value})}>
                  <option value="">Select property</option>
                  {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
              <Field label="Vendor (optional)">
                <select className="input-clean w-full" data-testid="expense-vendor-select" value={form.vendor_id} onChange={(e)=>pickVendor(e.target.value)}>
                  <option value="">— No vendor —</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}{v.category ? ` · ${v.category}` : ""}
                    </option>
                  ))}
                </select>
                {vendors.length === 0 && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    No vendors yet — add one on the Vendors page to start attributing expenses.
                  </p>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Category">
                  <select className="input-clean w-full" data-testid="expense-category-select" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Amount"><input required type="text" inputMode="decimal" className="input-clean w-full font-mono" data-testid="expense-amount-input" value={formatUSD(form.amount)} onChange={(e)=>setForm({...form, amount:parseUSD(e.target.value)})} placeholder="$0.00" /></Field>
              </div>
              <Field label="Date"><USDateInput required data-testid="expense-date-input" value={form.date} onChange={(iso)=>setForm({...form, date:iso})} /></Field>
              <Field label="Description"><textarea rows={2} className="input-clean w-full" data-testid="expense-desc-input" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} /></Field>
              <DialogFooter>
                <button type="button" onClick={()=>setOpen(false)} className="btn-outline-dark">Cancel</button>
                <button type="submit" className="btn-primary-dark" data-testid="expense-submit-button">{editingId ? "Save changes" : "Save"}</button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardShell>
  );
}

function Th({ children, className="" }) { return <th className={`px-5 py-3 overline text-slate-600 ${className}`}>{children}</th>; }
function Td({ children, className="" }) { return <td className={`px-5 py-4 ${className}`}>{children}</td>; }
function Field({ label, children }) { return <label className="block"><div className="overline text-slate-600 mb-1">{label}</div>{children}</label>; }
